<template>
  <CCardBody>
    <CDataTable
      class="custom-pagination"
      :items="ticketsData"
      :fields="tableFields"
      table-filter
      items-per-page-select
      :items-per-page="5"
      hover
      pagination
      responsive 
    >
      
      <template #ticketNumber="{ item }">
        <td>
          <div class="font-weight-bold text-primary">{{ item.ticketNumber || '-' }}</div>
        </td>
      </template>

      <template #assigned_date="{ item }">
        <td>
          <div v-if="item._role === 'reporter'">
            <div class="font-weight-bold table-text-main">
              {{ formatDate(item.userPart ? item.userPart.dateTimeInformed : '') }}
            </div>
            <div class="small text-muted table-text-sub">
              {{ $t('table.tech.reportedDateLabel') }}
            </div>
          </div>
          <div v-else>
            <div class="font-weight-bold table-text-main">
              {{ formatDate(item.supervisorPart ? item.supervisorPart.dateTimeInformed : '') }}
            </div>
            <div class="small text-muted table-text-sub">
              {{ $t('table.tech.byPrefix') }} {{ (item.supervisorPart && item.supervisorPart.supervisor) ? item.supervisorPart.supervisor.name : $t('table.tech.supervisorFallback') }}
            </div>
          </div>
        </td>
      </template>

      <template #issue_detail="{ item }">
        <td>
          <div class="font-weight-bold table-text-main">
            {{ getCategoryName(item) }} | {{ getSubCategoryName(item) }}
          </div>
          <div class="small text-muted table-text-sub">
            {{ $t('table.tech.reportedBy') }} {{ getUserName(item) }}
            <span v-if="item.userPart && item.userPart.onBehalfOf" class="text-danger">
              ({{ $t('table.admin.onBehalf') }} {{ item.userPart.onBehalfOf }})
            </span>
          </div>
          <span v-if="item._role === 'reporter'" class="role-badge badge-reporter">{{ $t('table.tech.badgeReporter') }}</span>
          <span v-else class="role-badge badge-assignee">{{ $t('table.tech.badgeAssignee') }}</span>
        </td>
      </template>

      <template #status="{ item }">
        <td class="text-center">
          <span
            class="status-circle"
            :style="{ backgroundColor: getBadge(item.status) }"
            :title="getStatusText(item.status)"
          ></span>
        </td>
      </template>

      <template #show_details="{ item }">
        <td class="text-center">
          <button
            class="circle-action-btn"
            @click="showDetail(item)"
            :title="getActionTitle(item.status)"
          >
            <CIcon :name="getActionIcon(item.status)" size="sm" />
          </button>
        </td>
      </template>
    </CDataTable>

    <div class="status-legend mt-3">
      <div class="legend-item">
        <span class="status-circle" style="background:#e55353"></span>
        <span>{{ $t('table.tech.legend1') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#fd7e14"></span>
        <span>{{ $t('table.tech.legend2') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#ffeb3b"></span>
        <span>{{ $t('table.tech.legend3') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#2eb85c"></span>
        <span>{{ $t('table.tech.legend4') }}</span>
      </div>
    </div>
  </CCardBody>
</template>

<style scoped>
/* สไตล์เดิม (คงเดิม) */
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  border-color: #2ba9a5 !important;
  color: #ffffff;
}
.custom-pagination .page-item .page-link {
  color: #414141;
}
.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
}

.status-circle {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.status-legend {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.status-legend .status-circle {
  width: 14px;
  height: 14px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #414141;
}

.circle-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #321fdb;
  background: transparent;
  color: #321fdb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}
.circle-action-btn:hover {
  background-color: #321fdb;
  color: #ffffff;
}

/* Media Query สำหรับมือถือ */
@media (max-width: 768px) {
  .table-text-main {
    font-size: 0.9rem;
    white-space: normal;
  }
  .table-text-sub {
    font-size: 0.75rem;
  }
  
  .status-circle {
    width: 24px;
    height: 24px;
  }
  
  .circle-action-btn {
    width: 30px;
    height: 30px;
  }
  
  .status-legend {
    gap: 12px;
  }
  .legend-item {
    font-size: 12px;
  }
}
.role-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 3px;
}

.badge-reporter {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.badge-assignee {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
</style>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "Treportable",
  data() {
    return {
      ticketsData: [],
    };
  },
  computed: {
    ...mapGetters("auth", ["profile"]),
    tableFields() {
      return [
        { key: "ticketNumber", label: this.$t("table.user.col.ticketNo"), _style: "width:15%; min-width: 120px;" },
        { key: "assigned_date", label: this.$t("table.tech.col.date"), _style: "width:20%; min-width: 110px;" },
        { key: "issue_detail", label: this.$t("table.tech.col.issueDetail"), _style: "width:35%; min-width: 200px;" },
        {
          key: "status",
          label: this.$t("table.user.col.status"),
          _style: "width:15%; text-align:center; min-width: 80px;",
          _classes: "text-center",
        },
        { key: "show_details", label: this.$t("table.admin.col.actions"), _style: "width:15%; text-align:center; min-width: 80px;" },
      ];
    },
  },
  watch: {
    profile(val) {
      if (val) this.loadTickets();
    }
  },
  mounted() {
    if (this.profile) {
      this.loadTickets();
    }
  },
  methods: {
    async loadTickets() {
      try {
        if (!this.profile) return;
        const technicianId = this.profile._id;

        // ดึงข้อมูลทั้ง 2 แหล่งพร้อมกัน
        const [assignedRes, reportedRes] = await Promise.all([
          axios.get(`${process.env.VUE_APP_APP_URL}/ticket/technician/${technicianId}/assigned`),
          fetch(`${process.env.VUE_APP_APP_URL}/ticket/user/${technicianId}/active`).then(r => r.json()),
        ]);

        let combined = [];

        // 1) งานที่ถูก assign (ไม่รวม welldone)
        if (assignedRes.data.success) {
          const assignedTickets = assignedRes.data.data
            .filter(t => t.status !== 'welldone')
            .map(t => ({ ...t, _role: 'assignee' }));
          combined = combined.concat(assignedTickets);
        }

        // 2) งานที่ช่างแจ้งเอง (active: pending/assigned/in_progress/done)
        if (reportedRes.success && Array.isArray(reportedRes.data)) {
          const existingIds = new Set(combined.map(t => String(t._id)));
          const reportedTickets = reportedRes.data
            .filter(t => !existingIds.has(String(t._id)))
            .map(t => ({ ...t, _role: 'reporter' }));
          combined = combined.concat(reportedTickets);
        }

        // เรียงตาม createdAt ล่าสุดก่อน
        combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        this.ticketsData = combined;

      } catch (error) {
        console.error("Failed to load technician tickets:", error);
      }
    },

    showDetail(item) {
      this.$emit("show-detail", item);
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}${this.$t('table.user.timeSuffix')}`;
    },

    getUserName(item) {
      if (item.username) return item.username;
      if (item.userPart && item.userPart.name) return item.userPart.name;
      if (item.userPart && item.userPart.user && item.userPart.user.name) {
        return `${item.userPart.user.name} ${item.userPart.user.surname || ''}`.trim();
      }
      return this.$t("table.tech.noUserName");
    },

    getCategoryName(item) {
      return (item.userPart && item.userPart.category) ? item.userPart.category.departmentName : this.$t("table.user.unknownDept");
    },

    getSubCategoryName(item) {
      const sub = item.userPart?.subCategory;
      
      if (!sub) return "-";

      if (typeof sub === 'object') {
        return sub.subCategory || sub.name || sub.title || sub.subcategoryName || "-";
      }

      return sub;
    },

    getStatusText(status) {
      const statusMap = {
        pending: this.$t("table.tech.status.pending"),
        assigned: this.$t("table.tech.status.assigned"),
        in_progress: this.$t("table.tech.status.in_progress"),
        done: this.$t("table.tech.status.done"),
        welldone: this.$t("table.tech.status.welldone"),
        cancelled: this.$t("table.tech.status.cancelled"),
      };
      return statusMap[status] || status;
    },

    getBadge(status) {
      const badgeMap = {
        pending: "#e55353",
        assigned: "#fd7e14",
        in_progress: "#ffeb3b",
        done: "#2eb85c",
        welldone: "#2eb85c",
        cancelled: "#cdcdcd",
      };
      return badgeMap[status] || "#adb5bd";
    },
    getActionIcon(status) {
      if (status === "assigned") return "cil-play";        
      if (status === "in_progress") return "cil-check";   
      return "cil-magnifying-glass";                       
    },
    getActionTitle(status) {
      if (status === "assigned") return this.$t("table.tech.action.acceptJob");
      if (status === "in_progress") return this.$t("table.tech.action.finishJob");
      return this.$t("table.tech.action.detail");
    },
  },
};
</script>